import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error.message);
  }
);

export const publicAPI = {
  getProfile: () => api.get('/public/profile'),
  getSkills: () => api.get('/public/skills'),
  getSkillsByCategory: (category) => api.get(`/public/skills/${category}`),
  getProjects: () => api.get('/public/projects'),
  getFeaturedProjects: () => api.get('/public/projects/featured'),
  getProjectBySlug: (slug) => api.get(`/public/projects/${slug}`),
  getProjectsByTechnology: (tech) => api.get(`/public/projects/technology/${tech}`),
  incrementProjectView: (id) => api.post(`/public/projects/${id}/view`),
  getExperiences: () => api.get('/public/experiences'),
  getEducation: () => api.get('/public/education'),
  getCertifications: () => api.get('/public/certifications'),
  getBlogs: (params) => api.get('/public/blogs', { params }),
  getBlogBySlug: (slug) => api.get(`/public/blogs/${slug}`),
  incrementBlogView: (id) => api.post(`/public/blogs/${id}/view`),
  getPublishedBlogs: () => api.get('/public/blogs/published'),
  sendContact: (data) => api.post('/public/contact', data),
};

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: () => api.get('/admin/analytics'),
  // Skills
  getSkills: () => api.get('/admin/skills'),
  getSkill: (id) => api.get(`/admin/skills/${id}`),
  createSkill: (data) => api.post('/admin/skills', data),
  updateSkill: (id, data) => api.put(`/admin/skills/${id}`, data),
  deleteSkill: (id) => api.delete(`/admin/skills/${id}`),
  // Projects
  getProjects: () => api.get('/admin/projects'),
  getProject: (id) => api.get(`/admin/projects/${id}`),
  createProject: (data) => api.post('/admin/projects', data),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  uploadThumbnail: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/projects/${id}/thumbnail`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadScreenshot: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/projects/${id}/screenshot`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Experience
  getExperiences: () => api.get('/admin/experiences'),
  getExperience: (id) => api.get(`/admin/experiences/${id}`),
  createExperience: (data) => api.post('/admin/experiences', data),
  updateExperience: (id, data) => api.put(`/admin/experiences/${id}`, data),
  deleteExperience: (id) => api.delete(`/admin/experiences/${id}`),
  // Education
  getEducation: () => api.get('/admin/education'),
  getEducationItem: (id) => api.get(`/admin/education/${id}`),
  createEducation: (data) => api.post('/admin/education', data),
  updateEducation: (id, data) => api.put(`/admin/education/${id}`, data),
  deleteEducation: (id) => api.delete(`/admin/education/${id}`),
  // Certifications
  getCertifications: () => api.get('/admin/certifications'),
  getCertification: (id) => api.get(`/admin/certifications/${id}`),
  createCertification: (data) => api.post('/admin/certifications', data),
  updateCertification: (id, data) => api.put(`/admin/certifications/${id}`, data),
  deleteCertification: (id) => api.delete(`/admin/certifications/${id}`),
  uploadCertImage: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/certifications/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Blogs
  getBlogs: (params) => api.get('/admin/blogs', { params }),
  getBlog: (id) => api.get(`/admin/blogs/${id}`),
  createBlog: (data) => api.post('/admin/blogs', data),
  updateBlog: (id, data) => api.put(`/admin/blogs/${id}`, data),
  deleteBlog: (id) => api.delete(`/admin/blogs/${id}`),
  uploadBlogImage: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/blogs/${id}/featured-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  togglePublish: (id) => api.post(`/admin/blogs/${id}/toggle-publish`),
  // Contacts
  getContacts: (params) => api.get('/admin/contacts', { params }),
  getContact: (id) => api.get(`/admin/contacts/${id}`),
  markAsRead: (id) => api.post(`/admin/contacts/${id}/read`),
  markAsReplied: (id) => api.post(`/admin/contacts/${id}/replied`),
  deleteContact: (id) => api.delete(`/admin/contacts/${id}`),
  getUnreadCount: () => api.get('/admin/contacts/unread-count'),
  // Profile
  getProfile: () => api.get('/admin/profile'),
  updateProfile: (data) => api.put('/admin/profile', data),
  uploadPhoto: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/admin/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadResume: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/admin/profile/resume', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export default api;
